import { type ComponentType, memo, type ReactNode, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useTrackShareClicked } from '@air/analytics';
import { LinkIcon, PlusIcon } from '@air/design-icons';
import {
  type DropdownMenuOption,
  type DropdownMenuProps,
  type RenderDropdownItemsParams,
} from '@air/primitive-dropdown-menu';

import { useGetPrivateShowcaseMenuOptions } from '~/hooks/useGetShowcaseMenuOptions';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { selectedBoardIdsForShowcaseSelector, selectedClipsAssetIdsSelector } from '~/store/selectedItems/selectors';
import { shouldShowExistingShowcasesMenuSection } from '~/utils/permissions/shortUrlPermissions';
import { canCreateCollection } from '~/utils/permissions/workspacePermissions';

type ShowcaseShareMenuComponent = ComponentType<DropdownMenuProps>;

type RenderShowcaseShareMenuItems = (params: RenderDropdownItemsParams) => ReactNode;

export type PrivateSelectionBarShowcaseShareMenuProps = {
  menu: ShowcaseShareMenuComponent;
  onCreateShowcase: () => void;
  renderItems: RenderShowcaseShareMenuItems;
  trigger: ReactNode;
  triggerTooltip: string;
};

/** Share CTA menu: add the selection to an existing showcase, or start a new one. */
export const PrivateSelectionBarShowcaseShareMenu = memo(
  ({
    menu: Menu,
    onCreateShowcase,
    renderItems,
    trigger,
    triggerTooltip,
  }: PrivateSelectionBarShowcaseShareMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const { trackShareClicked } = useTrackShareClicked();
    const assetIds = useSelector(selectedClipsAssetIdsSelector);
    const boardIds = useSelector(selectedBoardIdsForShowcaseSelector);
    const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
    const { editableShortUrls, isFetching, onAddToShowcase } = useGetPrivateShowcaseMenuOptions();

    const options = useMemo(() => {
      const menuOptions: DropdownMenuOption[] = [];
      const canCreateShowcase = canCreateCollection(workspacePermissions);
      const showExistingSection =
        editableShortUrls.length > 0 &&
        shouldShowExistingShowcasesMenuSection({
          debouncedSearch: '',
          editableShortUrlsCount: editableShortUrls.length,
          isFetching,
          workspacePermissions,
        });

      if (showExistingSection) {
        menuOptions.push({
          className: 'font-bold text-10 uppercase tracking-[0.4px]',
          id: 'add-to-existing-showcase',
          label: 'Add to existing showcase',
          type: 'title',
        });

        for (const shortUrl of editableShortUrls) {
          menuOptions.push({
            id: shortUrl.collection.id,
            label: shortUrl.collection.name,
            onSelect: () => onAddToShowcase(shortUrl.id, shortUrl.collection.id, { assetIds, boardIds }),
            prefix: <LinkIcon size="sm" />,
            type: 'item',
          });
        }
      }

      if (canCreateShowcase) {
        if (showExistingSection) {
          menuOptions.push({
            id: 'new-showcase-separator',
            type: 'separator',
          });
        }

        menuOptions.push({
          id: 'new-share-link',
          label: 'New showcase',
          onSelect: onCreateShowcase,
          prefix: <PlusIcon size="sm" />,
          type: 'item',
        });
      }

      return menuOptions;
    }, [assetIds, boardIds, editableShortUrls, isFetching, onAddToShowcase, onCreateShowcase, workspacePermissions]);

    const handleOpenChange = useCallback(
      (nextOpen: boolean) => {
        setIsOpen(nextOpen);

        if (nextOpen) {
          trackShareClicked({ location: 'action_bar' });
        }
      },
      [trackShareClicked],
    );

    return (
      <Menu
        align="end"
        data-testid="SELECTION_BAR_SHOWCASE_SHARE_MENU"
        onOpenChange={handleOpenChange}
        open={isOpen}
        side="top"
        sideOffset={16}
        trigger={trigger}
        triggerTooltip={triggerTooltip}
      >
        {renderItems({ options })}
      </Menu>
    );
  },
);

PrivateSelectionBarShowcaseShareMenu.displayName = 'PrivateSelectionBarShowcaseShareMenu';
